/**
 * notifRouteResolver.js
 * ─────────────────────
 * Maps backend notification { type, route } to the correct dashboard tab key
 * for each role. Call resolveNotifRoute(notification, role) wherever you need
 * to navigate after a notification click.
 *
 * Backend sends `route` directly when it knows the target tab.
 * This utility normalises it per-role so clicks always land on the right tab.
 */

// ── Per-role tab-key maps ────────────────────────────────────────────────────
// Keys are `route` values (or `type` fallbacks) from the backend.
// Values are the exact case/string used in each dashboard's switch().

const ROLE_MAPS = {
  employee: {
    chat:                   'chat',
    helpdesk:               'helpdesk',
    leave:                  'leave',
    permission:             'leave',
    payroll:                'payroll',
    calendar:               'calendar',
    event:                  'calendar',
    reminder:               'calendar',
    announcement:           'announcements',
    announcements:          'announcements',
    notifications:          'announcements',
    onboarding:             'onboarding',
    offboarding:            'offboarding',
    assets:                 'assets',
    asset:                  'assets',
    performance:            'performance',
    policies:               'policies',
    profile:                'profile',
    dashboard:              'dashboard',
    general:                'announcements',
  },

  intern: {
    chat:                   'chat',
    helpdesk:               'helpdesk',
    leave:                  'leave',
    permission:             'leave',
    calendar:               'calendar',
    event:                  'calendar',
    reminder:               'calendar',
    announcement:           'announcements',
    announcements:          'announcements',
    notifications:          'announcements',
    onboarding:             'onboarding',
    offboarding:            'offboarding',
    policies:               'policies',
    profile:                'profile',
    dashboard:              'dashboard',
    general:                'announcements',
  },

  hr: {
    chat:                   'chat',
    tickets:                'tickets',
    helpdesk:               'tickets',
    requests:               'tickets',
    leave:                  'leave',
    permission:             'leave',
    payroll:                'payroll',
    calendar:               'calendar',
    event:                  'calendar',
    announcement:           'announcements',
    announcements:          'announcements',
    notifications:          'notifications',
    onboarding:             'employees',
    offboarding:            'offboarding',
    reports:                'reports',
    lost_asset:             'reports',
    advances:               'advances',
    permissions:            'leave',
    audit:                  'audit',
    dashboard:              'dashboard',
    general:                'notifications',
  },

  itadmin: {
    chat:                   'chat',
    requests:               'requests',
    helpdesk:               'requests',
    tickets:                'requests',
    assets:                 'assets',
    asset:                  'assets',
    maintenance:            'assets',
    lost_asset:             'assets',
    credentials:            'credentials',
    onboarding:             'credentials',
    exit:                   'exit',
    calendar:               'calendar',
    event:                  'calendar',
    announcement:           'announce',
    announcements:          'announce',
    announce:               'announce',
    evaluation:             'announce',
    notifications:          'notifications',
    dashboard:              'dashboard',
    general:                'notifications',
  },

  pm: {
    chat:                   'chat',
    tickets:                'tickets',
    helpdesk:               'tickets',
    requests:               'tickets',
    leave:                  'leave',
    permission:             'leave',
    permissions:            'permissions',
    payroll:                'reports',
    reports:                'reports',
    calendar:               'calendar',
    event:                  'calendar',
    announcement:           'announce',
    announcements:          'announce',
    announce:               'announce',
    evaluation:             'evaluations',
    evaluations:            'evaluations',
    project:                'projects',
    projects:               'projects',
    lost_asset:             'lostreports',
    lostreports:            'lostreports',
    advances:               'advances',
    hrrequests:             'hrrequests',
    offboarding:            'offboardingmanagement',
    offboardingmanagement:  'offboardingmanagement',
    notifications:          'notifications',
    dashboard:              'dashboard',
    general:                'notifications',
  },

  superadmin: {
    // Super admin only has one notifications page
    default:                'notifications',
  },
};

/**
 * Resolves a notification to the correct tab key for the given role.
 *
 * @param {object} notif  - Notification object { type, route, ... }
 * @param {string} role   - One of: employee, intern, hr, itadmin, pm, superadmin
 * @returns {string}      - Tab key string to pass to setActive / onNavigate
 */
export function resolveNotifRoute(notif, role) {
  if (!notif) return 'dashboard';
  const map = ROLE_MAPS[role?.toLowerCase()] || ROLE_MAPS.employee;

  // Backend `route` field takes priority — it's the most explicit signal.
  const routeVal = String(notif.route || '').toLowerCase().trim();
  if (routeVal && routeVal !== 'null' && map[routeVal]) return map[routeVal];

  // Fall back to `type`
  const typeVal = String(notif.type || '').toLowerCase().trim();
  if (typeVal && map[typeVal]) return map[typeVal];

  // Super admin: always go to notifications
  if (role?.toLowerCase() === 'superadmin') return 'notifications';

  // Last resort
  return map['general'] || map['notifications'] || 'dashboard';
}
